/**
 * FDI tooth numbering. Section 3.1, `domain/teeth/`.
 *
 * Upper arch 18..11 then 21..28 curves over the top, lower arch 48..41 then
 * 31..38 mirrors it below, so the chart reads the way a dentist reads the
 * mouth: the patient's right is on the viewer's left. Ported from the
 * prototype's ARCH_U and ARCH_L.
 *
 * The chart is drawn in four sections, the way a dental chart is: upper right,
 * upper left, lower right, lower left. `SECTIONS` is that order, and `spanTeeth`
 * is the run of teeth a bridge or a titanium bar covers along an arch.
 */

export const ARCH_UPPER = [
  '18',
  '17',
  '16',
  '15',
  '14',
  '13',
  '12',
  '11',
  '21',
  '22',
  '23',
  '24',
  '25',
  '26',
  '27',
  '28',
] as const;

export const ARCH_LOWER = [
  '48',
  '47',
  '46',
  '45',
  '44',
  '43',
  '42',
  '41',
  '31',
  '32',
  '33',
  '34',
  '35',
  '36',
  '37',
  '38',
] as const;

export type ToothNumber = (typeof ARCH_UPPER)[number] | (typeof ARCH_LOWER)[number];

export const ALL_TEETH: readonly ToothNumber[] = [...ARCH_UPPER, ...ARCH_LOWER];

export type Quadrant = 1 | 2 | 3 | 4;
export type Arch = 'upper' | 'lower';
export type ToothType = 'incisor' | 'canine' | 'premolar' | 'molar';

/**
 * The five silhouettes the chart draws. Incisors are two shapes: the central is
 * broader than the lateral, and on a chart of thirty two teeth that difference
 * is what tells 11 from 12 at a glance.
 */
export type ToothGlyph = 'molar' | 'premolar' | 'canine' | 'central' | 'lateral';

export function isToothNumber(value: string): value is ToothNumber {
  return (ALL_TEETH as readonly string[]).includes(value);
}

function parse(tooth: ToothNumber): { quadrant: Quadrant; position: number } {
  // Every FDI permanent tooth is two digits: quadrant then position 1 to 8.
  const quadrant = Number(tooth[0]) as Quadrant;
  const position = Number(tooth[1]);
  return { quadrant, position };
}

export function quadrantOf(tooth: ToothNumber): Quadrant {
  return parse(tooth).quadrant;
}

export function archOf(tooth: ToothNumber): Arch {
  const { quadrant } = parse(tooth);
  return quadrant === 1 || quadrant === 2 ? 'upper' : 'lower';
}

export function toothTypeOf(tooth: ToothNumber): ToothType {
  const { position } = parse(tooth);
  if (position <= 2) return 'incisor';
  if (position === 3) return 'canine';
  if (position <= 5) return 'premolar';
  return 'molar';
}

export function toothGlyphOf(tooth: ToothNumber): ToothGlyph {
  const { position } = parse(tooth);
  if (position === 1) return 'central';
  if (position === 2) return 'lateral';
  return toothTypeOf(tooth) as Exclude<ToothGlyph, 'central' | 'lateral'>;
}

export type SectionKey = 'ur' | 'ul' | 'lr' | 'll';

export interface Section {
  key: SectionKey;
  label: string;
  teeth: readonly ToothNumber[];
}

/**
 * The four sections of the chart, in the order it draws them.
 *
 * Each runs the way its arch runs: the right sections from the back tooth to
 * the midline, the left from the midline to the back, so the two sections of an
 * arch read as one row with the midline between them.
 */
export const SECTIONS: readonly Section[] = [
  { key: 'ur', label: 'Upper right', teeth: ARCH_UPPER.slice(0, 8) },
  { key: 'ul', label: 'Upper left', teeth: ARCH_UPPER.slice(8) },
  { key: 'lr', label: 'Lower right', teeth: ARCH_LOWER.slice(0, 8) },
  { key: 'll', label: 'Lower left', teeth: ARCH_LOWER.slice(8) },
];

/**
 * The teeth a span covers, from `from` to `to` inclusive, in arch order.
 *
 * Either end may be named first: 11 to 13 and 13 to 11 are the same bridge.
 * The run follows the arch and not the numbers, so 13 to 23 covers 13, 12, 11,
 * 21, 22, 23. A pair on different arches has no run between them, and the
 * contract refuses it before it gets here.
 */
export function spanTeeth(from: ToothNumber, to: ToothNumber): ToothNumber[] {
  if (archOf(from) !== archOf(to)) {
    throw new Error(`A span cannot run from ${from} to ${to}: the two are on different arches`);
  }
  const arch: readonly ToothNumber[] = archOf(from) === 'upper' ? ARCH_UPPER : ARCH_LOWER;
  const a = arch.indexOf(from);
  const b = arch.indexOf(to);
  return arch.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/** Sorted into arch order, so a selection always reads left to right on the chart. */
export function sortTeeth(teeth: readonly string[]): string[] {
  const order = new Map(ALL_TEETH.map((tooth, index) => [tooth as string, index]));
  return [...teeth].sort((a, b) => (order.get(a) ?? Infinity) - (order.get(b) ?? Infinity));
}
